import React from "react";
import { PageMetadataType } from "@swo/storefront";
import { createJpgLink } from "../_shared-components/image-related/createJpgAndWebpLinks";
import { pipe } from "fp-ts/lib/function";
import { option as O } from "fp-ts";

export function PageMetadataComponent(
  props: PageMetadataType & {
    languageIsoCode: string;
    locationOrigin: URL;
    fallbackImageContentfulUrl?: string;
    canonicalPath?: string;
    forceNoIndex?: boolean;
  },
) {
  const { title, metaDescription, metaKeywords, ogImage } = props;

  const ogTitle = props.ogTitle || title;
  const ogDescription = props.ogDescription || metaDescription;

  const ogImageWithFallback = pipe(
    O.fromNullable(props.ogImage?.url),
    O.alt(() => O.fromNullable(props.fallbackImageContentfulUrl)),
    O.map(url => createJpgLink(url, 1200)),
    O.toUndefined,
  );

  const maybeCanonical = props.canonicalPath
    ? new URL(props.canonicalPath, props.locationOrigin).toString()
    : props.canonicalPath;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={metaDescription} />

      {maybeCanonical && <link rel="canonical" href={maybeCanonical} />}

      {metaKeywords && <meta name={"keywords"} content={metaKeywords.join(",")} />}
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:site_name" content={props.officialCompanyName} />

      {ogImageWithFallback && <meta property="og:image" content={ogImageWithFallback} />}

      {props.forceNoIndex || props.noIndex ? (
        <meta name="robots" content="noindex" />
      ) : (
        <meta name="robots" content="index,follow" />
      )}

      <meta property="og:type" content="website" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />

      {ogImageWithFallback && <meta name="twitter:image" content={ogImageWithFallback} />}
      <meta name="twitter:description" content={ogDescription} />
      {ogImage && (ogImage.description || ogImage.title) && (
        <meta name="twitter:image:alt" content={ogImage.description || ogImage.title} />
      )}
      <meta name="twitter:site" content={props.companyTwitterAccount} />

      {props.hrefLangData.map(({ localeCode, path }) => (
        <link
          key={localeCode}
          rel="alternate"
          hrefLang={localeCode}
          href={new URL(path, props.locationOrigin).toString()}
        />
      ))}
    </>
  );
}
